import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "@src/packages/api/louise-api";
import { throwApiError } from "@src/packages/api/api-error";
import type { paths } from "@src/packages/openapi/schema";
import type { RequestBody, ResponseBody } from "@src/packages/openapi/helpers";

// The generated client is typed from the unversioned default OpenAPI
// document; this hook pins x-api-version 2026-08-27, whose responses are
// settings-only. Narrow to that contract so dropped persistence metadata
// (id, userId, timestamps) cannot be read.
const apiVersionHeaders = { "x-api-version": "2026-08-27" } as const;

export type PrivacyData = Pick<
  ResponseBody<paths["/me/privacy"]["get"]>["data"],
  "shareTestResultsWithProvider" | "participateInResearch"
>;

type UpdatePrivacyPayload = RequestBody<paths["/me/privacy"]["patch"]>;

function privacyQueryKey(patientId: string | undefined) {
  // The query cache outlives a sign-out, so an unscoped key would show the
  // previous patient's consent choices to the next account on this device.
  return ["privacy", patientId ?? "unauthenticated"] as const;
}

export function usePrivacy() {
  const { client, patientId } = useApiClient();

  return useQuery<PrivacyData>({
    queryKey: privacyQueryKey(patientId),
    enabled: Boolean(patientId),
    queryFn: async () => {
      const { data, error, response } = await client.GET("/me/privacy", {
        headers: apiVersionHeaders,
      });
      if (data) return data.data;
      throwApiError(
        error,
        "Failed to load privacy settings",
        "usePrivacy",
        response?.status,
      );
    },
  });
}

export function useUpdatePrivacy() {
  const { client, patientId } = useApiClient();
  const queryClient = useQueryClient();
  const queryKey = privacyQueryKey(patientId);

  return useMutation({
    mutationFn: async (payload: UpdatePrivacyPayload) => {
      const result = await client.PATCH("/me/privacy", {
        body: payload,
        headers: apiVersionHeaders,
      });
      const error = result.error;

      if (error) {
        throwApiError(
          error,
          "Failed to update privacy settings",
          "useUpdatePrivacy",
          result.response?.status,
        );
      }

      return result;
    },
    onMutate: (payload: UpdatePrivacyPayload) => {
      const previous = queryClient.getQueryData<PrivacyData>(queryKey);

      // Begin cancellation, but do not wait for a slow in-flight fetch before
      // reflecting the patient's explicit choice in the control.
      void queryClient.cancelQueries({ queryKey });
      queryClient.setQueryData<PrivacyData>(queryKey, (current) => {
        if (!current) return current;
        return { ...current, ...payload };
      });
      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });
}
